import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Always send credentials (httpOnly cookies) with axios requests
axios.defaults.withCredentials = true;

// Format API error detail
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Check admin auth via httpOnly cookie
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Admin auth check failed:", error);
      }
      setUser(false);
    }

    // Check coach auth via httpOnly cookie
    try {
      const response = await axios.get(`${API}/coach-auth/me`, { withCredentials: true });
      setCoach(response.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Coach auth check failed:", error);
      }
      setCoach(false);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Admin login
  const login = async (email, password) => {
    try {
      await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      const meResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(meResponse.data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  // Admin logout
  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Admin logout request failed:", err);
    } finally {
      setUser(false);
    }
  };

  // Coach login
  const coachLogin = async (email, password) => {
    try {
      await axios.post(
        `${API}/coach-auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // Always fetch full profile to guarantee complete state (docs, rcpro_number, etc.)
      const meResponse = await axios.get(`${API}/coach-auth/me`, { withCredentials: true });
      setCoach(meResponse.data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  // Coach logout
  const coachLogout = async () => {
    try {
      await axios.post(`${API}/coach-auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Coach logout request failed:", err);
    } finally {
      setCoach(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      coach,
      loading, 
      login, 
      logout, 
      coachLogin,
      coachLogout,
      checkAuth 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
